import React, { useState, useEffect, useRef } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Plus, Search, Package, Edit, Trash2, Calendar, AlertCircle, Eye, RefreshCw, Filter, LayoutGrid, List, Table as TableIcon, X, ArrowUpDown, ChevronUp, ChevronDown, ArrowUp, ArrowDown, QrCode, ScanLine, Download, Terminal, Users, ChevronRight as ChevronRightIcon } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatInCST } from '../utils/timezone';
import AssetDetailDialog from '../components/AssetDetailDialog';
import BarcodeScanner from '../components/BarcodeScanner';
import BulkScriptExecution from '../components/BulkScriptExecution';
import { useAuth } from '../contexts/AuthContext';
import { assetWidthStorageKey } from '../utils/assetPrefs';

export default function Assets() {
  const { user } = useAuth();
  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState(() => window.innerWidth < 768 ? 'card' : 'table'); // card, list, table - default to card on mobile
  const [showFilters, setShowFilters] = useState(false);
  const [showBulkExec, setShowBulkExec] = useState(false);
  const [sortBy, setSortBy] = useState(() => localStorage.getItem('assets.sortBy') || 'created_at'); // name, created_at, customer_name, status
  const [sortOrder, setSortOrder] = useState(() => localStorage.getItem('assets.sortOrder') || 'desc'); // asc, desc
  const [filters, setFilters] = useState({
    status: '',
    customer_id: '',
    asset_type: '',
    has_ninjarmm: '',
    has_bitdefender: '',
    has_screenconnect: ''
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [labelAsset, setLabelAsset] = useState(null);
  const [labelConfig, setLabelConfig] = useState({ label_type: 'qr', size: 300, include_logo: true, fg_color: '#000000', bg_color: '#FFFFFF' });
  const [generatedLabel, setGeneratedLabel] = useState(null);
  const [generatingLabel, setGeneratingLabel] = useState(false);
  const [bulkLabelOpen, setBulkLabelOpen] = useState(false);
  const [bulkLabels, setBulkLabels] = useState([]);
  const [selectedForBulk, setSelectedForBulk] = useState([]);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    asset_type: 'device',
    customer_id: '',
    serial_number: '',
    model: '',
    manufacturer: '',
    status: 'active',
    location: '',
    purchase_date: '',
    warranty_expiry: '',
    notes: ''
  });

  // req6: resizable + persistent asset table columns
  const ASSET_COLUMNS = [
    { key: 'name', label: 'Name', sortKey: 'name', def: 220 },
    { key: 'type', label: 'Type', sortKey: 'asset_type', def: 110 },
    { key: 'serial', label: 'Serial Number', sortKey: 'serial_number', def: 150 },
    { key: 'customer', label: 'Customer', sortKey: 'customer_name', def: 170 },
    { key: 'status', label: 'Status', sortKey: 'status', def: 120 },
    { key: 'location', label: 'Location', sortKey: 'location', def: 130 },
    { key: 'integrations', label: 'Integrations', sortKey: 'integrations', def: 200 },
    { key: 'warranty', label: 'Warranty', sortKey: 'warranty_expiry', def: 130 },
    { key: 'created', label: 'Created', sortKey: 'created_at', def: 120 },
    { key: 'actions', label: 'Actions', def: 140 },
  ];
  // Column widths persist per CURRENT USER (not a shared key) so one tech's layout
  // never overwrites another's on a shared machine.
  const widthStorageKey = assetWidthStorageKey(user);
  const [colWidths, setColWidths] = useState(() => Object.fromEntries(ASSET_COLUMNS.map(c => [c.key, c.def])));
  useEffect(() => {
    const defaults = Object.fromEntries(ASSET_COLUMNS.map(c => [c.key, c.def]));
    try {
      const saved = JSON.parse(localStorage.getItem(widthStorageKey) || '{}');
      setColWidths({ ...defaults, ...saved });
    } catch { setColWidths(defaults); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [widthStorageKey]);

  // Set true for the brief window right after a real (moved) resize gesture so the
  // synthetic TH click fired on mouse-release — even when released over the header
  // label — does not trigger a sort. The next intentional click still sorts.
  const justResizedRef = useRef(false);
  const startResize = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = colWidths[key] || 120;
    let moved = false;
    const onMove = (ev) => {
      moved = true;
      const w = Math.max(60, startW + (ev.clientX - startX));
      setColWidths(prev => ({ ...prev, [key]: w }));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      if (moved) {
        justResizedRef.current = true;
        setTimeout(() => { justResizedRef.current = false; }, 250);
        setColWidths(prev => {
          localStorage.setItem(widthStorageKey, JSON.stringify(prev));
          return prev;
        });
      }
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  // Shared sort click guard: swallow the click that ends a resize drag.
  const handleHeaderSort = (sortKey) => {
    if (justResizedRef.current) { justResizedRef.current = false; return; }
    handleColumnSort(sortKey);
  };

  useEffect(() => {
    localStorage.setItem('assets.sortBy', sortBy);
    localStorage.setItem('assets.sortOrder', sortOrder);
  }, [sortBy, sortOrder]);

  useEffect(() => {
    loadAssets();
    loadCustomers();
  }, [searchTerm, filters, sortBy, sortOrder]);

  const loadAssets = async () => {
    try {
      setLoading(true);
      const params = {};
      if (searchTerm) params.search = searchTerm;
      if (filters.status) params.status = filters.status;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (filters.asset_type) params.asset_type = filters.asset_type;
      if (filters.has_ninjarmm !== '') params.has_ninjarmm = filters.has_ninjarmm === 'true';
      if (filters.has_bitdefender !== '') params.has_bitdefender = filters.has_bitdefender === 'true';
      if (filters.has_screenconnect !== '') params.has_screenconnect = filters.has_screenconnect === 'true';
      params.sort_by = sortBy;
      params.sort_order = sortOrder;
      
      const response = await api.get('/assets', { params });
      setAssets(response.data);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      const dataToSubmit = {
        ...formData,
        customer_name: customer?.company_name || ''
      };
      
      // Remove empty strings
      Object.keys(dataToSubmit).forEach(key => {
        if (dataToSubmit[key] === '') delete dataToSubmit[key];
      });
      
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, dataToSubmit);
        toast.success('Asset updated successfully');
      } else {
        await api.post('/assets', dataToSubmit);
        toast.success('Asset created successfully');
      }
      setDialogOpen(false);
      resetForm();
      loadAssets();
    } catch (error) {
      const errorMessage = error.response?.data?.detail;
      if (Array.isArray(errorMessage)) {
        toast.error(errorMessage.map(e => e.msg).join(', '));
      } else {
        toast.error(errorMessage || 'An error occurred');
      }
    }
  };

  const handleEdit = (asset) => {
    setEditingAsset(asset);
    setFormData({
      name: asset.name,
      asset_type: asset.asset_type || 'device',
      customer_id: asset.customer_id || '',
      serial_number: asset.serial_number || '',
      model: asset.model || '',
      manufacturer: asset.manufacturer || '',
      status: asset.status,
      location: asset.location || '',
      purchase_date: asset.purchase_date ? new Date(asset.purchase_date).toISOString().split('T')[0] : '',
      warranty_expiry: asset.warranty_expiry ? new Date(asset.warranty_expiry).toISOString().split('T')[0] : '',
      notes: asset.notes || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this asset?')) return;
    
    try {
      await api.delete(`/assets/${assetId}`);
      toast.success('Asset deleted successfully');
      loadAssets();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      asset_type: 'device',
      customer_id: '',
      serial_number: '',
      model: '',
      manufacturer: '',
      status: 'active',
      location: '',
      purchase_date: '',
      warranty_expiry: '',
      notes: ''
    });
    setEditingAsset(null);
  };

  const handleDialogChange = (open) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const handleViewDetails = (asset) => {
    setSelectedAsset(asset);
    setDetailDialogOpen(true);
  };

  const handleGenerateLabel = async (asset) => {
    setLabelAsset(asset);
    setGeneratedLabel(null);
    setLabelDialogOpen(true);
  };

  const doGenerateLabel = async () => {
    if (!labelAsset) return;
    setGeneratingLabel(true);
    try {
      const res = await api.post('/generate-asset-label', {
        asset_id: labelAsset.id,
        label_type: labelConfig.label_type,
        size: labelConfig.size,
        include_logo: labelConfig.include_logo,
        fg_color: labelConfig.fg_color,
        bg_color: labelConfig.bg_color,
      });
      setGeneratedLabel(res.data);
    } catch { toast.error('Failed to generate label'); }
    setGeneratingLabel(false);
  };

  const handleDownloadLabel = () => {
    if (!generatedLabel) return;
    const link = document.createElement('a');
    link.href = generatedLabel.image;
    link.download = `${generatedLabel.asset_name || 'asset'}-label.png`;
    link.click();
  };

  const handleBulkGenerate = async () => {
    if (selectedForBulk.length === 0) return;
    setGeneratingLabel(true);
    try {
      const res = await api.post('/generate-bulk-labels', {
        asset_ids: selectedForBulk,
        label_type: labelConfig.label_type,
        size: 200,
        include_logo: labelConfig.include_logo,
        fg_color: labelConfig.fg_color,
        bg_color: labelConfig.bg_color,
      });
      setBulkLabels(res.data.labels || []);
      setBulkLabelOpen(true);
    } catch { toast.error('Failed to generate bulk labels'); }
    setGeneratingLabel(false);
  };

  const handlePrintLabels = () => {
    const labels = bulkLabels.length > 0 ? bulkLabels : (generatedLabel ? [generatedLabel] : []);
    if (labels.length === 0) return;
    const win = window.open('', '_blank');
    win.document.write('<html><head><title>Asset Labels</title><style>body{font-family:sans-serif;text-align:center;} .label{display:inline-block;margin:12px;padding:10px;border:1px solid #ddd;border-radius:8px;} .label img{max-width:200px;} .label p{margin:4px 0;font-size:11px;}</style></head><body>');
    labels.forEach(l => {
      win.document.write(`<div class="label"><img src="${l.image}"/><p><b>${l.asset_name}</b></p><p>SN: ${l.serial_number || '—'}</p></div>`);
    });
    win.document.write('</body></html>');
    win.document.close();
    win.print();
  };

  const handleAssetUpdate = () => {
    loadAssets();
  };

  const handleSyncFromNinjaRMM = async () => {
    try {
      // Start the sync
      await api.post('/ninjarmm/discover-assets');
      
      toast.info('NinjaRMM asset discovery started. Check the NinjaRMM page for progress.', { duration: 5000 });
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 300; // 5 minutes max
      
      const checkStatus = setInterval(async () => {
        try {
          const statusRes = await api.get('/ninjarmm/sync-status');
          
          if (statusRes.data.status === 'completed') {
            clearInterval(checkStatus);
            toast.success(
              `NinjaRMM Sync Complete! ${statusRes.data.created || 0} created, ${statusRes.data.updated || 0} updated`,
              { duration: 5000 }
            );
            loadAssets();
          } else if (statusRes.data.status === 'error') {
            clearInterval(checkStatus);
            toast.error('NinjaRMM sync failed');
            loadAssets();
          } else if (attempts >= maxAttempts) {
            clearInterval(checkStatus);
            toast.warning('Sync is taking longer than expected. Check NinjaRMM page for status.');
            loadAssets();
          }
          
          attempts++;
        } catch (error) {
          console.error('Failed to check sync status:', error);
        }
      }, 1000);
      
    } catch (error) {
      toast.error('Failed to start NinjaRMM sync');
    }
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      customer_id: '',
      asset_type: '',
      has_ninjarmm: '',
      has_bitdefender: '',
      has_screenconnect: ''
    });
  };

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const handleColumnSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-gray-400" />;
    }
    return sortOrder === 'asc' ? 
      <ArrowUp className="h-3.5 w-3.5 text-blue-600" /> : 
      <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
  };

  const getStatusColor = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800 hover:bg-green-200 transition-colors',
      inactive: 'bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors',
      maintenance: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors',
      retired: 'bg-red-100 text-red-800 hover:bg-red-200 transition-colors'
    };
    return colors[status] || colors.active;
  };

  const isWarrantyExpiring = (warrantyDate) => {
    if (!warrantyDate) return false;
    const expiry = new Date(warrantyDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry > 0 && daysUntilExpiry <= 30;
  };

  const isWarrantyExpired = (warrantyDate) => {
    if (!warrantyDate) return false;
    return new Date(warrantyDate) < new Date();
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="assets-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Assets</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Manage hardware, software, and equipment inventory</p>
        </div>
        <div className="flex gap-2">
          <Button 
            variant="outline" 
            onClick={handleSyncFromNinjaRMM}
            data-testid="sync-ninjarmm-button"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Sync from NinjaRMM
          </Button>
          <Button variant="outline" onClick={() => setShowBulkExec(true)} data-testid="bulk-execute-button">
            <Terminal className="h-4 w-4 mr-2" />
            Bulk Execute
          </Button>
          <Button variant="outline" onClick={() => {
            const ids = assets.map(a => a.id);
            setSelectedForBulk(ids.slice(0, 50));
            handleBulkGenerate();
          }} data-testid="bulk-labels-btn" disabled={generatingLabel}>
            <QrCode className="h-4 w-4 mr-2" />
            {generatingLabel ? 'Generating...' : 'Bulk Labels'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={handleDialogChange}>
            <DialogTrigger asChild>
              <Button data-testid="add-asset-button">
                <Plus className="h-4 w-4 mr-2" />
                Add Asset
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingAsset ? 'Edit Asset' : 'Add New Asset'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label htmlFor="name">Asset Name *</Label>
                  <Input
                    id="name"
                    data-testid="asset-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="asset_type">Asset Type</Label>
                  <Select
                    value={formData.asset_type}
                    onValueChange={(value) => setFormData({ ...formData, asset_type: value })}
                  >
                    <SelectTrigger data-testid="asset-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="device">Device</SelectItem>
                      <SelectItem value="server">Server</SelectItem>
                      <SelectItem value="network">Network Equipment</SelectItem>
                      <SelectItem value="software">Software License</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="status">Status</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                      <SelectItem value="maintenance">Maintenance</SelectItem>
                      <SelectItem value="retired">Retired</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="customer_id">Customer</Label>
                  <Select
                    value={formData.customer_id}
                    onValueChange={(value) => setFormData({ ...formData, customer_id: value })}
                  >
                    <SelectTrigger data-testid="customer-select">
                      <SelectValue placeholder="Select customer (optional)" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>
                          {customer.company_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="serial_number">Serial Number</Label>
                  <div className="flex gap-1.5">
                    <Input
                      id="serial_number"
                      value={formData.serial_number}
                      onChange={(e) => setFormData({ ...formData, serial_number: e.target.value })}
                      className="flex-1"
                    />
                    <BarcodeScanner onScan={(val) => setFormData(prev => ({ ...prev, serial_number: val }))} />
                  </div>
                </div>
                <div>
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({ ...formData, manufacturer: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="location">Location</Label>
                  <Input
                    id="location"
                    value={formData.location}
                    onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="purchase_date">Purchase Date</Label>
                  <Input
                    id="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={(e) => setFormData({ ...formData, purchase_date: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor="warranty_expiry">Warranty Expiry</Label>
                  <Input
                    id="warranty_expiry"
                    type="date"
                    value={formData.warranty_expiry}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="notes">Notes</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={3}
                  />
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => handleDialogChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-asset-button">
                  {editingAsset ? 'Update' : 'Create'} Asset
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Controls Bar: Search, Sort, Filters, View Mode */}
      <div className="flex items-center gap-3 flex-wrap">
        {/* Search */}
        <div className="flex items-center gap-2 max-w-sm flex-1">
          <Search className="h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search assets..."
            data-testid="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="border-neutral-300 focus:border-brand-primary"
          />
          <BarcodeScanner
            onScan={(val) => setSearchTerm(val)}
            buttonVariant="outline"
            buttonSize="icon"
          />
        </div>

        {/* Sort Controls */}
        <div className="flex items-center gap-2">
          <Label className="text-sm font-medium text-gray-700">Sort by:</Label>
          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px] h-9" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name</SelectItem>
              <SelectItem value="created_at">Created Date</SelectItem>
              <SelectItem value="customer_name">Customer</SelectItem>
              <SelectItem value="status">Status</SelectItem>
            </SelectContent>
          </Select>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
            data-testid="sort-order-toggle"
            className="h-9 px-2"
          >
            <ArrowUpDown className="h-4 w-4" />
          </Button>
        </div>

        {/* Filter Toggle */}
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="filter-toggle"
          className="h-9"
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <Badge className="ml-2 bg-blue-600 text-white text-xs">
              {Object.values(filters).filter(f => f !== '').length}
            </Badge>
          )}
        </Button>

        {/* Quick Filter: Unprotected Assets */}
        <Button
          variant="outline"
          onClick={() => {
            setFilters({
              ...filters,
              has_ninjarmm: 'true',
              has_bitdefender: 'false'
            });
            setShowFilters(true);
          }}
          className="h-9 border-red-300 text-red-700 hover:bg-red-50"
          data-testid="unprotected-filter"
        >
          <AlertCircle className="h-4 w-4 mr-2" />
          No Antivirus
        </Button>

        {/* UX3.4: Quick filter chips */}
        <div className="flex gap-1 flex-wrap" data-testid="quick-filters">
          {[
            { label: 'Servers', key: 'asset_type', value: 'server', testId: 'qf-servers' },
            { label: 'Workstations', key: 'asset_type', value: 'workstation', testId: 'qf-workstations' },
            { label: 'Online', key: 'status', value: 'active', testId: 'qf-online' },
            { label: 'Offline', key: 'status', value: 'inactive', testId: 'qf-offline' },
          ].map(qf => {
            const isActive = filters[qf.key] === qf.value;
            return (
              <button
                key={qf.testId}
                onClick={() => {
                  setFilters(prev => ({
                    ...prev,
                    [qf.key]: isActive ? '' : qf.value,
                  }));
                }}
                className={`h-8 px-3 text-xs rounded-full border transition-colors ${
                  isActive
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid={qf.testId}
              >
                {qf.label}
              </button>
            );
          })}
        </div>

        {/* View Mode Switcher */}
        <div className="flex gap-1 border rounded-md p-1 bg-white">
          <Button
            size="sm"
            variant={viewMode === 'card' ? 'default' : 'ghost'}
            onClick={() => setViewMode('card')}
            data-testid="card-view-button"
            className="h-8 px-3"
            title="Card view"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'list' ? 'default' : 'ghost'}
            onClick={() => setViewMode('list')}
            data-testid="list-view-button"
            className="h-8 px-3"
            title="List view"
          >
            <List className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'table' ? 'default' : 'ghost'}
            onClick={() => setViewMode('table')}
            data-testid="table-view-button"
            className="h-8 px-3"
            title="Table view"
          >
            <TableIcon className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant={viewMode === 'grouped' ? 'default' : 'ghost'}
            onClick={() => {
              setViewMode('grouped');
              // Auto-expand all customers on first switch
              if (Object.keys(expandedCustomers).length === 0) {
                const expanded = {};
                const names = [...new Set(assets.map(a => a.customer_name || 'Unassigned'))];
                names.forEach(n => { expanded[n] = true; });
                setExpandedCustomers(expanded);
              }
            }}
            data-testid="grouped-view-button"
            className="h-8 px-3"
            title="Group by customer"
          >
            <Users className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Status</Label>
                <Select 
                  value={filters.status || undefined} 
                  onValueChange={(value) => setFilters({...filters, status: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="retired">Retired</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Customer</Label>
                <Select 
                  value={filters.customer_id || undefined} 
                  onValueChange={(value) => setFilters({...filters, customer_id: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-customer">
                    <SelectValue placeholder="All customers" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(customer => (
                      <SelectItem key={customer.id} value={customer.id}>
                        {customer.company_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">Asset Type</Label>
                <Select 
                  value={filters.asset_type || undefined} 
                  onValueChange={(value) => setFilters({...filters, asset_type: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-asset-type">
                    <SelectValue placeholder="All types" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="device">Device</SelectItem>
                    <SelectItem value="server">Server</SelectItem>
                    <SelectItem value="network">Network Equipment</SelectItem>
                    <SelectItem value="software">Software License</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">NinjaRMM</Label>
                <Select 
                  value={filters.has_ninjarmm || undefined} 
                  onValueChange={(value) => setFilters({...filters, has_ninjarmm: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-ninjarmm">
                    <SelectValue placeholder="All assets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Synced</SelectItem>
                    <SelectItem value="false">Not Synced</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">BitDefender</Label>
                <Select 
                  value={filters.has_bitdefender || undefined} 
                  onValueChange={(value) => setFilters({...filters, has_bitdefender: value || ''})}
                >
                  <SelectTrigger className="h-9 bg-white" data-testid="filter-bitdefender">
                    <SelectValue placeholder="All assets" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Connected</SelectItem>
                    <SelectItem value="false">Not Connected</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="text-xs font-semibold mb-1 block">ScreenConnect</Label>
                <div className="flex gap-2">
                  <Select 
                    value={filters.has_screenconnect || undefined} 
                    onValueChange={(value) => setFilters({...filters, has_screenconnect: value || ''})}
                  >
                    <SelectTrigger className="h-9 bg-white" data-testid="filter-screenconnect">
                      <SelectValue placeholder="All assets" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="true">Connected</SelectItem>
                      <SelectItem value="false">Not Connected</SelectItem>
                    </SelectContent>
                  </Select>
                  {hasActiveFilters && (
                    <Button 
                      size="sm" 
                      variant="ghost" 
                      onClick={clearFilters} 
                      className="h-9 px-2"
                      data-testid="clear-filters-button"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-6">
          {loading ? (
            <div className="text-center py-8">
              <img 
                src="/cipherone-logo.png" 
                alt="Loading" 
                className="h-12 w-12 logo-spin mx-auto mb-2"
              />
              <div>Loading assets...</div>
            </div>
          ) : assets.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Package className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p>No assets found. {hasActiveFilters ? 'Try adjusting your filters.' : 'Add your first asset!'}</p>
            </div>
          ) : viewMode === 'card' ? (
            /* Card View */
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assets.map((asset) => (
                <Card key={asset.id} className="hover:shadow-md transition-shadow" data-testid="asset-card">
                  <CardContent className="p-4">
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg flex items-center gap-2">
                          <Package className="h-5 w-5 text-gray-600" />
                          {asset.name}
                        </h3>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          <Badge className={getStatusColor(asset.status)}>
                            {asset.status}
                          </Badge>
                          {asset.ninjarmm_device_id && (
                            <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs hover:bg-blue-200 transition-colors">
                              NinjaRMM
                            </Badge>
                          )}
                          {asset.bitdefender_endpoint_id && (
                            <Badge variant="custom" className="bg-purple-100 text-purple-800 text-xs hover:bg-purple-200 transition-colors">
                              BitDefender
                            </Badge>
                          )}
                          {asset.screenconnect_session_id && (
                            <Badge variant="custom" className="bg-green-100 text-green-800 text-xs hover:bg-green-200 transition-colors">
                              ScreenConnect
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleViewDetails(asset)}
                          data-testid={`view-asset-details-${asset.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Details
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(asset)}
                          data-testid="edit-asset-button"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(asset.id)}
                          data-testid="delete-asset-button"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    </div>
                    <div className="space-y-2 text-sm">
                      {asset.asset_type && (
                        <p className="text-gray-600">
                          <span className="font-medium">Type:</span> {asset.asset_type}
                        </p>
                      )}
                      {asset.customer_name && (
                        <p className="text-gray-600">
                          <span className="font-medium">Customer:</span> {asset.customer_name}
                        </p>
                      )}
                      {asset.manufacturer && (
                        <p className="text-gray-600">
                          <span className="font-medium">Make:</span> {asset.manufacturer} {asset.model}
                        </p>
                      )}
                      {asset.serial_number && (
                        <p className="text-gray-600 font-mono text-xs">
                          SN: {asset.serial_number}
                        </p>
                      )}
                      {asset.location && (
                        <p className="text-gray-600">
                          <span className="font-medium">Location:</span> {asset.location}
                        </p>
                      )}
                      {asset.warranty_expiry && (
                        <div className={`flex items-center gap-1 ${
                          isWarrantyExpired(asset.warranty_expiry) ? 'text-red-600' :
                          isWarrantyExpiring(asset.warranty_expiry) ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          {(isWarrantyExpired(asset.warranty_expiry) || isWarrantyExpiring(asset.warranty_expiry)) && (
                            <AlertCircle className="h-4 w-4" />
                          )}
                          <Calendar className="h-4 w-4" />
                          <span className="text-xs">
                            Warranty: {formatInCST(asset.warranty_expiry, 'MMM d, yyyy')}
                            {isWarrantyExpired(asset.warranty_expiry) && ' (Expired)'}
                            {isWarrantyExpiring(asset.warranty_expiry) && ' (Expiring Soon)'}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 pt-3 border-t text-xs text-gray-500">
                      Added {formatInCST(asset.created_at, 'MMM d, yyyy')}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : viewMode === 'list' ? (
            /* List View */
            <div className="space-y-2">
              {assets.map((asset) => (
                <Card 
                  key={asset.id} 
                  className="hover:shadow-sm transition-shadow cursor-pointer border-l-4"
                  style={{
                    borderLeftColor: 
                      asset.status === 'active' ? '#22c55e' :
                      asset.status === 'maintenance' ? '#f59e0b' :
                      asset.status === 'retired' ? '#ef4444' :
                      '#6b7280'
                  }}
                  onClick={() => handleViewDetails(asset)}
                  data-testid="asset-list-row"
                >
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between gap-4">
                      <div className="flex items-center gap-4 flex-1 min-w-0">
                        <Package className="h-5 w-5 text-gray-600 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-base truncate">{asset.name}</h3>
                          <div className="flex items-center gap-2 text-sm text-gray-600 flex-wrap">
                            <span>{asset.asset_type || 'Unknown'}</span>
                            {asset.customer_name && (
                              <>
                                <span>•</span>
                                <span>{asset.customer_name}</span>
                              </>
                            )}
                            {asset.serial_number && (
                              <>
                                <span>•</span>
                                <span className="font-mono text-xs">{asset.serial_number}</span>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Badge className={getStatusColor(asset.status)}>
                          {asset.status}
                        </Badge>
                        
                        <div className="flex gap-1">
                          {asset.ninjarmm_device_id && (
                            <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs hover:bg-blue-200 transition-colors">NinjaRMM</Badge>
                          )}
                          {asset.bitdefender_endpoint_id && (
                            <Badge variant="custom" className="bg-purple-100 text-purple-800 text-xs hover:bg-purple-200 transition-colors">BitDefender</Badge>
                          )}
                          {asset.screenconnect_session_id && (
                            <Badge variant="custom" className="bg-green-100 text-green-800 text-xs hover:bg-green-200 transition-colors">ScreenConnect</Badge>
                          )}
                        </div>
                        
                        {asset.warranty_expiry && (isWarrantyExpired(asset.warranty_expiry) || isWarrantyExpiring(asset.warranty_expiry)) && (
                          <AlertCircle className={`h-4 w-4 ${isWarrantyExpired(asset.warranty_expiry) ? 'text-red-600' : 'text-yellow-600'}`} />
                        )}
                        
                        <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(asset)}
                            data-testid="edit-asset-button"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(asset.id)}
                            data-testid="delete-asset-button"
                          >
                            <Trash2 className="h-4 w-4 text-red-600" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleGenerateLabel(asset)}
                            data-testid="generate-label-btn"
                            title="Generate barcode/QR label"
                          >
                            <QrCode className="h-4 w-4 text-purple-600" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : viewMode === 'grouped' ? (
            /* Grouped by Customer View */
            (() => {
              const grouped = {};
              assets.forEach(asset => {
                const key = asset.customer_name || 'Unassigned';
                if (!grouped[key]) grouped[key] = [];
                grouped[key].push(asset);
              });
              const sortedNames = Object.keys(grouped).sort((a, b) => {
                if (a === 'Unassigned') return 1;
                if (b === 'Unassigned') return -1;
                return a.localeCompare(b);
              });
              return (
                <div className="space-y-3" data-testid="grouped-assets-view">
                  <div className="flex items-center justify-between text-xs text-gray-500 px-1">
                    <span>{sortedNames.length} customer{sortedNames.length !== 1 ? 's' : ''} · {assets.length} asset{assets.length !== 1 ? 's' : ''}</span>
                    <div className="flex gap-2">
                      <button
                        className="hover:text-gray-800 hover:underline"
                        onClick={() => {
                          const all = {};
                          sortedNames.forEach(n => { all[n] = true; });
                          setExpandedCustomers(all);
                        }}
                      >Expand all</button>
                      <button
                        className="hover:text-gray-800 hover:underline"
                        onClick={() => setExpandedCustomers({})}
                      >Collapse all</button>
                    </div>
                  </div>
                  {sortedNames.map(customerName => {
                    const custAssets = grouped[customerName];
                    const isExpanded = expandedCustomers[customerName] !== false;
                    return (
                      <Card key={customerName} className="overflow-hidden" data-testid={`customer-group-${customerName}`}>
                        <button
                          type="button"
                          className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left border-b"
                          onClick={() => setExpandedCustomers(prev => ({ ...prev, [customerName]: !isExpanded }))}
                        >
                          <div className="flex items-center gap-2">
                            <ChevronRightIcon className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                            <Users className="h-4 w-4 text-gray-500" />
                            <span className="font-semibold text-sm">{customerName}</span>
                            <Badge variant="secondary" className="text-xs ml-1">{custAssets.length}</Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            {custAssets.filter(a => a.ninjarmm_device_id).length > 0 && (
                              <Badge variant="custom" className="bg-blue-50 text-blue-700 text-xs">{custAssets.filter(a => a.ninjarmm_device_id).length} NinjaRMM</Badge>
                            )}
                            {custAssets.filter(a => a.status === 'active').length > 0 && (
                              <span className="text-green-600">{custAssets.filter(a => a.status === 'active').length} active</span>
                            )}
                          </div>
                        </button>
                        {isExpanded && (
                          <Table>
                            <TableHeader>
                              <TableRow className="bg-white">
                                {ASSET_COLUMNS.filter(c => c.key !== 'customer' && c.key !== 'created').map(c => (
                                  <TableHead
                                    key={c.key}
                                    className={`font-medium text-xs whitespace-nowrap select-none ${c.sortKey ? 'cursor-pointer hover:bg-neutral-100 transition-colors' : ''}`}
                                    onClick={c.sortKey ? () => handleHeaderSort(c.sortKey) : undefined}
                                    data-testid={c.sortKey ? `group-sort-header-${c.key}` : `group-col-head-${c.key}`}
                                  >
                                    <div className="flex items-center gap-1">
                                      <span className="whitespace-nowrap">{c.label}</span>
                                      {c.sortKey && getSortIcon(c.sortKey)}
                                    </div>
                                  </TableHead>
                                ))}
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {custAssets.map(asset => (
                                <TableRow
                                  key={asset.id}
                                  className="cursor-pointer hover:bg-neutral-50 border-b border-neutral-100"
                                  onClick={() => handleViewDetails(asset)}
                                  data-testid="asset-table-row"
                                >
                                  <TableCell className="font-semibold text-sm">
                                    <div className="flex items-center gap-2">
                                      <Package className="h-4 w-4 text-gray-500" />
                                      {asset.name}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-xs text-gray-600">{asset.asset_type || '-'}</TableCell>
                                  <TableCell className="font-mono text-xs text-gray-600">{asset.serial_number || '-'}</TableCell>
                                  <TableCell>
                                    <Badge className={getStatusColor(asset.status)}>{asset.status}</Badge>
                                  </TableCell>
                                  <TableCell className="text-xs text-gray-600">{asset.location || '-'}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1 flex-wrap">
                                      {asset.ninjarmm_device_id && <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs">NinjaRMM</Badge>}
                                      {asset.bitdefender_endpoint_id && <Badge variant="custom" className="bg-purple-100 text-purple-800 text-xs">BitDefender</Badge>}
                                      {asset.screenconnect_session_id && <Badge variant="custom" className="bg-green-100 text-green-800 text-xs">ScreenConnect</Badge>}
                                      {asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && (
                                        <Badge variant="custom" className="bg-red-100 text-red-800 text-xs flex items-center gap-1">
                                          <AlertCircle className="h-3 w-3" /> No AV
                                        </Badge>
                                      )}
                                      {!asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && !asset.screenconnect_session_id && (
                                        <span className="text-xs text-gray-400">None</span>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    {asset.warranty_expiry ? (
                                      <span className={`text-xs ${isWarrantyExpired(asset.warranty_expiry) ? 'text-red-600' : isWarrantyExpiring(asset.warranty_expiry) ? 'text-yellow-600' : 'text-gray-600'}`}>
                                        {formatInCST(asset.warranty_expiry, 'MMM d, yyyy')}
                                      </span>
                                    ) : <span className="text-xs text-gray-400">-</span>}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                                      <Button size="sm" variant="ghost" onClick={() => handleEdit(asset)}><Edit className="h-4 w-4" /></Button>
                                      <Button size="sm" variant="ghost" onClick={() => handleDelete(asset.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                      <Button size="sm" variant="ghost" onClick={() => handleGenerateLabel(asset)} title="Generate label"><QrCode className="h-4 w-4 text-purple-600" /></Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        )}
                      </Card>
                    );
                  })}
                </div>
              );
            })()
          ) : (
            /* Table View */
            <Table className="table-fixed" style={{ minWidth: ASSET_COLUMNS.reduce((s, c) => s + (colWidths[c.key] || c.def), 0) }} data-testid="assets-table">
              <colgroup>
                {ASSET_COLUMNS.map(c => (
                  <col key={c.key} style={{ width: colWidths[c.key] || c.def }} />
                ))}
              </colgroup>
              <TableHeader>
                <TableRow className="bg-neutral-50">
                  {ASSET_COLUMNS.map(c => (
                    <TableHead
                      key={c.key}
                      className={`font-semibold relative select-none ${c.sortKey ? 'cursor-pointer hover:bg-neutral-100 transition-colors' : ''}`}
                      onClick={c.sortKey ? () => handleHeaderSort(c.sortKey) : undefined}
                      data-testid={c.sortKey ? `sort-header-${c.key}` : `col-head-${c.key}`}
                    >
                      <div className="flex items-center gap-2 overflow-hidden pr-1.5">
                        <span className="truncate whitespace-nowrap">{c.label}</span>
                        {c.sortKey && getSortIcon(c.sortKey)}
                      </div>
                      <div
                        onMouseDown={(e) => startResize(e, c.key)}
                        onClick={(e) => e.stopPropagation()}
                        className="absolute top-0 right-0 h-full w-1.5 cursor-col-resize hover:bg-blue-400/60 active:bg-blue-500"
                        data-testid={`col-resize-${c.key}`}
                        title="Drag to resize"
                      />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {assets.map((asset) => (
                  <TableRow 
                    key={asset.id} 
                    className="cursor-pointer hover:bg-neutral-50 border-b border-neutral-100"
                    onClick={() => handleViewDetails(asset)}
                    data-testid="asset-table-row"
                  >
                    <TableCell className="font-semibold">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-gray-600" />
                        {asset.name}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {asset.asset_type || 'Unknown'}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-600">
                      {asset.serial_number || '-'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {asset.customer_name || 'No customer'}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusColor(asset.status)}>
                        {asset.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {asset.location || '-'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap">
                        {asset.ninjarmm_device_id && (
                          <Badge variant="custom" className="bg-blue-100 text-blue-800 text-xs hover:bg-blue-200 transition-colors">NinjaRMM</Badge>
                        )}
                        {asset.bitdefender_endpoint_id && (
                          <Badge variant="custom" className="bg-purple-100 text-purple-800 text-xs hover:bg-purple-200 transition-colors">BitDefender</Badge>
                        )}
                        {asset.screenconnect_session_id && (
                          <Badge variant="custom" className="bg-green-100 text-green-800 text-xs hover:bg-green-200 transition-colors">ScreenConnect</Badge>
                        )}
                        {/* Warning for assets with NinjaRMM but no BitDefender */}
                        {asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && (
                          <Badge variant="custom" className="bg-red-100 text-red-800 text-xs flex items-center gap-1 hover:bg-red-200 transition-colors">
                            <AlertCircle className="h-3 w-3" />
                            No Antivirus
                          </Badge>
                        )}
                        {!asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && !asset.screenconnect_session_id && (
                          <span className="text-xs text-gray-400">None</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {asset.warranty_expiry ? (
                        <div className={`flex items-center gap-1 text-xs ${
                          isWarrantyExpired(asset.warranty_expiry) ? 'text-red-600' :
                          isWarrantyExpiring(asset.warranty_expiry) ? 'text-yellow-600' : 'text-gray-600'
                        }`}>
                          {(isWarrantyExpired(asset.warranty_expiry) || isWarrantyExpiring(asset.warranty_expiry)) && (
                            <AlertCircle className="h-3 w-3" />
                          )}
                          {formatInCST(asset.warranty_expiry, 'MMM d, yyyy')}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {formatInCST(asset.created_at, 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(asset)}
                          data-testid="edit-asset-button"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDelete(asset.id)}
                          data-testid="delete-asset-button"
                        >
                          <Trash2 className="h-4 w-4 text-red-600" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => handleGenerateLabel(asset)} data-testid="generate-label-btn" title="Generate label">
                          <QrCode className="h-4 w-4 text-purple-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <AssetDetailDialog
        asset={selectedAsset}
        open={detailDialogOpen}
        onOpenChange={setDetailDialogOpen}
        onUpdate={handleAssetUpdate}
      />

      {/* Label Generator Dialog */}
      <Dialog open={labelDialogOpen} onOpenChange={setLabelDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><QrCode className="h-5 w-5" /> Generate Asset Label</DialogTitle>
          </DialogHeader>
          {labelAsset && (
            <div className="space-y-4">
              <div className="text-sm bg-gray-50 p-3 rounded-lg">
                <p className="font-semibold">{labelAsset.name}</p>
                <p className="text-gray-500">SN: {labelAsset.serial_number || '—'} | Type: {labelAsset.asset_type}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Label Type</Label>
                  <Select value={labelConfig.label_type} onValueChange={(v) => { setLabelConfig(c => ({ ...c, label_type: v })); setGeneratedLabel(null); }}>
                    <SelectTrigger data-testid="label-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="qr">QR Code (with logo)</SelectItem>
                      <SelectItem value="barcode">Barcode (Code 128)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Size</Label>
                  <Select value={String(labelConfig.size)} onValueChange={(v) => { setLabelConfig(c => ({ ...c, size: parseInt(v) })); setGeneratedLabel(null); }}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="200">Small (200px)</SelectItem>
                      <SelectItem value="300">Medium (300px)</SelectItem>
                      <SelectItem value="400">Large (400px)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {labelConfig.label_type === 'qr' && (
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <Label>Foreground</Label>
                    <Input type="color" value={labelConfig.fg_color} onChange={(e) => { setLabelConfig(c => ({ ...c, fg_color: e.target.value })); setGeneratedLabel(null); }} />
                  </div>
                  <div>
                    <Label>Background</Label>
                    <Input type="color" value={labelConfig.bg_color} onChange={(e) => { setLabelConfig(c => ({ ...c, bg_color: e.target.value })); setGeneratedLabel(null); }} />
                  </div>
                  <div className="flex items-end">
                    <div className="flex items-center gap-2">
                      <Checkbox checked={labelConfig.include_logo} onCheckedChange={(c) => { setLabelConfig(lc => ({ ...lc, include_logo: !!c })); setGeneratedLabel(null); }} data-testid="include-logo-check" />
                      <Label className="text-sm">CipherOne Logo</Label>
                    </div>
                  </div>
                </div>
              )}
              <div className="flex gap-2">
                <Button onClick={doGenerateLabel} disabled={generatingLabel} className="flex-1" data-testid="do-generate-label-btn">
                  {generatingLabel ? 'Generating...' : 'Generate'}
                </Button>
                {generatedLabel && (
                  <>
                    <Button variant="outline" onClick={handleDownloadLabel} data-testid="download-label-btn"><Download className="h-4 w-4 mr-1.5" />Download</Button>
                    <Button variant="outline" onClick={handlePrintLabels} data-testid="print-label-btn">Print</Button>
                  </>
                )}
              </div>
              {generatedLabel && (
                <div className="flex flex-col items-center p-4 border rounded-lg bg-white" data-testid="generated-label-preview">
                  <img src={generatedLabel.image} alt="Asset Label" className="max-w-[300px]" />
                  <p className="font-semibold mt-2">{generatedLabel.asset_name}</p>
                  <p className="text-sm text-gray-500">SN: {generatedLabel.serial_number || '—'}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Labels Dialog */}
      <Dialog open={bulkLabelOpen} onOpenChange={setBulkLabelOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Bulk Asset Labels ({bulkLabels.length})</DialogTitle>
          </DialogHeader>
          <div className="flex justify-end gap-2 mb-3">
            <Button onClick={handlePrintLabels} variant="outline" data-testid="bulk-print-btn">Print All</Button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
            {bulkLabels.map((label, idx) => (
              <div key={idx} className="flex flex-col items-center p-3 border rounded-lg">
                <img src={label.image} alt={label.asset_name} className="max-w-[150px]" />
                <p className="font-semibold text-sm mt-1 text-center truncate w-full">{label.asset_name}</p>
                <p className="text-xs text-gray-500">SN: {label.serial_number || '—'}</p>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      <BulkScriptExecution open={showBulkExec} onOpenChange={setShowBulkExec} />
    </div>
  );
}
